// client/src/layouts/DashboardLayout.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Outlet, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { purgeOfflineCaches } from "../serviceWorkerRegistration";
import StaleDataNotice from "../components/StaleDataNotice";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Divider,
  Tooltip,
  CssBaseline,
  CircularProgress,
  Container,
  useTheme,
  useMediaQuery,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NotificationsIcon from "@mui/icons-material/Notifications";
import HistoryIcon from "@mui/icons-material/History";
import MailIcon from "@mui/icons-material/Mail";
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CloseIcon from '@mui/icons-material/Close';
// import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import { UserContext } from '../contexts/UserContext';
import roleMenus from '../config/roleMenus';
import { SIDEBAR } from '../themes';
// The year being viewed against the school's own current one — see the helper
// for why the web could not tell them apart before.
import { isPastAcademicYear, onAcademicYearChanged } from '../utils/academicYearContext';
import useScopeKey from '../hooks/useScopeKey';
import { fetchJson } from '../utils/fetchJson';
import { useAccess } from '../contexts/AccessContext';
import { releaseStuckOverlays, startOverlayWatchdog } from '../utils/uiLock';
import { deepOrange } from '@mui/material/colors';
import { hasPageAccess } from '../config/pagePermissions';
import { usePhase } from '../contexts/PhaseContext';
import YearSelector from '../components/Common/YearSelector';
import PhaseSwitcher from '../components/PhaseSwitcher';
import { registerCurrentUser, onEvent } from '../services/socket';
import SubscriptionRenewalPage from '../pages/School_Admin/Finance/SubscriptionRenewalPage';
import usePageTracking from '../hooks/usePageTracking';
import useSessionManager from '../hooks/useSessionManager';
import { usePlatform } from '../contexts/PlatformContext';
// Shared with the phone (packages/core/src/ui/badge.js). MUI's own `max` prop
// renders "99+" and stretches the disc to fit three characters; the phone caps
// at 9. Same inbox, two different numbers, on a control whose entire job is to
// state a number.
import { formatBadgeCount, UNREAD_BADGE } from '../utils/badgeCount.generated';
import { AndroidAppButton } from '../components/AndroidAppDownload';
import { describeNotification } from '../utils/notificationText.generated';

// Screens that render money figures. Matched broadly so a finance page added later
// is covered by default rather than silently shipping cached balances unmarked.
const FINANCE_ROUTES = /(finance|financial|fee-|fees|payment|payroll|salary|invoice|budget|expense|subscription-renewal)/i;

// Cached endpoints those screens draw on, used to date-stamp the offline copy.
const FINANCE_CACHE_PREFIXES = [
  '/api/finance',
  '/api/invoices',
  '/api/payments',
  '/api/payroll',
  '/api/budgets',
  '/api/expenses',
  '/api/fee-types',
  '/api/subscriptions',
];

// Define expanded and collapsed widths
const drawerExpandedWidth = 240;
const drawerCollapsedWidth = 64;

export default function DashboardLayout({ themeName, onThemeChange }) {
  usePageTracking();
  const isMobile = useMediaQuery('(max-width:600px)');
  // The breathing room the content keeps on a phone. Small enough that the width
  // is still the screen's, large enough that nothing touches the glass — and the
  // SAME on both sides, which is the part that was wrong.
  //
  // A CSS STRING, NOT A NUMBER, and the difference is not cosmetic. In MUI's
  // `sx`, padding and margin values go through the theme's spacing transform:
  // a bare `12` means 12 SPACING UNITS, which createTheme resolves to 96px. On a
  // 360px screen that is 192px of gutter and 168px of content — the whole page
  // squeezed into a column down the middle, which is what a raw 12 did here.
  const MOBILE_GUTTER = '10px';
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Recomputed on every year change rather than read once: the switcher can
  // move between sessions without a reload, and a banner that only appeared on
  // a fresh page load would be worse than none.
  const [viewingPastYear, setViewingPastYear] = useState(() => isPastAcademicYear());
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  // Messages UI state
  const [msgUnread, setMsgUnread] = useState(0);
  const [msgList, setMsgList] = useState([]);
  const [msgAnchor, setMsgAnchor] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [teacherClasses, setTeacherClasses] = useState(null);
  const [announceSnack, setAnnounceSnack] = useState({ open: false, title: '', body: '' });
  const [messageSnack, setMessageSnack] = useState({ open: false, fromName: '', fromAvatarUrl: '', subject: '', preview: '' });
  const [notifSnack, setNotifSnack] = useState({ open: false, title: '', message: '', source: '' });
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionBilling, setSubscriptionBilling] = useState(null);
  const [subscriptionInvoice, setSubscriptionInvoice] = useState(null);
  const [subscriptionPaymentsSummary, setSubscriptionPaymentsSummary] = useState(null);
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const user = useContext(UserContext);
  useSessionManager(user);

  // Routes that display money figures drawn from cached endpoints.
  const isFinanceRoute = FINANCE_ROUTES.test(location.pathname || '');

  const roleNormForSubscription = (user?.role || '').toString();
  const canManageSubscriptionForSubscription = roleNormForSubscription === 'SchoolAdmin' || roleNormForSubscription === 'Principal' || roleNormForSubscription === 'HeadMaster';
  const subscriptionFetchedOnceRef = useRef(false);
  const subscriptionDialogDismissedRef = useRef(false);

  const loadSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError('');
      const resp = await fetchJson('/api/subscriptions/me', { method: 'GET' });
      const sub = resp?.subscription || resp?.data?.subscription || resp?.data || null;
      setSubscription(sub);
      setSubscriptionBilling(resp?.billing || null);
      setSubscriptionInvoice(resp?.latestInvoice || null);
      setSubscriptionPaymentsSummary(resp?.paymentsSummary || null);
    } catch (e) {
      setSubscriptionError(e?.message || 'Failed to load subscription');
      setSubscription(null);
      setSubscriptionBilling(null);
      setSubscriptionInvoice(null);
      setSubscriptionPaymentsSummary(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleOpenSubscription = async () => {
    subscriptionDialogDismissedRef.current = false;
    setSubscriptionOpen(true);
    await loadSubscription();
  };

  const handleCloseSubscription = () => {
    subscriptionDialogDismissedRef.current = true;
    setSubscriptionOpen(false);
  };

  const subscriptionUi = useMemo(() => {
    try {
      const msPerDay = 1000 * 60 * 60 * 24;
      const nowMs = Date.now();
      const GRACE_DAYS = 14;

      const parseMs = (d) => {
        if (!d) return null;
        const t = new Date(d).getTime();
        return Number.isFinite(t) ? t : null;
      };

      const addDaysMs = (ms, days) => {
        if (!Number.isFinite(ms)) return null;
        return ms + (Number(days || 0) * msPerDay);
      };

      const baseStatusRaw = (subscription?.status || '').toString().trim().toLowerCase();
      const startMs = parseMs(subscription?.startDate);
      const endMs = parseMs(subscription?.endDate);
      const graceEndMsExplicit = parseMs(subscription?.graceEndDate);
      const graceEndMs = graceEndMsExplicit || (endMs ? addDaysMs(endMs, GRACE_DAYS) : null);

      const isTrial = baseStatusRaw === 'trial';

      const isSuspended = baseStatusRaw === 'suspended';
      const isExpired = endMs ? endMs < nowMs : false;
      const isInGrace = isExpired && graceEndMs ? graceEndMs >= nowMs : false;

      const effectiveStatus = isSuspended
        ? 'suspended'
        : (isInGrace
            ? 'grace'
            : (isExpired
                ? 'expired'
                : (baseStatusRaw || 'active')));

      const deadlineMs = effectiveStatus === 'grace' ? graceEndMs : endMs;
      const timeLeftDays = deadlineMs ? Math.ceil((deadlineMs - nowMs) / msPerDay) : null;

      const formatTimeLeft = () => {
        if (!Number.isFinite(deadlineMs)) return '-';
        const diffMs = Math.max(0, deadlineMs - nowMs);
        const d = Math.floor(diffMs / msPerDay);
        const h = Math.floor((diffMs - d * msPerDay) / (1000 * 60 * 60));
        if (d <= 0) return `${h} hour(s)`;
        if (h <= 0) return `${d} day(s)`;
        return `${d} day(s) ${h} hour(s)`;
      };

      const periodBounds = (() => {
        if (effectiveStatus === 'grace') {
          const graceStart = endMs;
          const graceEnd = graceEndMs;
          if (Number.isFinite(graceStart) && Number.isFinite(graceEnd) && graceEnd > graceStart) {
            const totalDays = Math.max(1, Math.ceil((graceEnd - graceStart) / msPerDay));
            const elapsedDays = Math.max(0, Math.floor((nowMs - graceStart) / msPerDay));
            const remainingDays = Math.ceil((graceEnd - nowMs) / msPerDay);
            const percent = Math.min(100, Math.max(0, ((nowMs - graceStart) / (graceEnd - graceStart)) * 100));
            return { percent, elapsedDays, remainingDays, totalDays, label: 'Grace period' };
          }
          return { percent: null, elapsedDays: null, remainingDays: null, totalDays: null, label: 'Grace period' };
        }

        if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
          const totalDays = Math.max(1, Math.ceil((endMs - startMs) / msPerDay));
          const elapsedDays = Math.max(0, Math.floor((nowMs - startMs) / msPerDay));
          const remainingDays = Math.ceil((endMs - nowMs) / msPerDay);
          const percent = Math.min(100, Math.max(0, ((nowMs - startMs) / (endMs - startMs)) * 100));
          return { percent, elapsedDays, remainingDays, totalDays, label: isTrial ? 'Trial period' : 'Subscription' };
        }
        return { percent: null, elapsedDays: null, remainingDays: null, totalDays: null, label: isTrial ? 'Trial period' : 'Subscription' };
      })();

      const severity = (() => {
        if (effectiveStatus === 'suspended') return 'error';
        if (effectiveStatus === 'expired') return 'error';
        if (effectiveStatus === 'grace') return 'warning';
        if (Number.isFinite(timeLeftDays) && timeLeftDays <= 7) return 'warning';
        return 'info';
      })();

      const statusLabel = effectiveStatus === 'grace' ? 'Expired (Grace)' : (isTrial && effectiveStatus === 'trial' ? 'Trial' : effectiveStatus);
      const deadlineLabel = effectiveStatus === 'grace' ? 'Grace ends' : (isTrial && effectiveStatus === 'trial' ? 'Trial ends' : 'Expires');
      const actionLabel = (() => {
        if (effectiveStatus === 'suspended') return 'Access is suspended. Renew to restore access.';
        if (effectiveStatus === 'expired') return 'Subscription expired. Renew to regain access.';
        if (effectiveStatus === 'grace') return 'Your subscription is overdue. Renew before grace ends to avoid suspension.';
        if (isTrial && Number.isFinite(timeLeftDays) && timeLeftDays <= 7) return 'Your free trial is close to ending. Renew to avoid interruption.';
        if (Number.isFinite(timeLeftDays) && timeLeftDays <= 7) return 'Your subscription is close to expiring. Renew to avoid interruption.';
        return null;
      })();

      return {
        effectiveStatus,
        isTrial,
        statusLabel,
        severity,
        startMs,
        endMs,
        graceEndMs,
        deadlineMs,
        deadlineLabel,
        timeLeftDays,
        timeLeftText: formatTimeLeft(),
        periodBounds,
        actionLabel
      };
    } catch (_) {
      return {
        effectiveStatus: null,
        isTrial: false,
        statusLabel: null,
        severity: 'info',
        startMs: null,
        endMs: null,
        graceEndMs: null,
        deadlineMs: null,
        deadlineLabel: 'Expires',
        timeLeftDays: null,
        timeLeftText: '-',
        periodBounds: { percent: null, elapsedDays: null, remainingDays: null, totalDays: null, label: 'Subscription' },
        actionLabel: null
      };
    }
  }, [subscription]);

  const { activePhase } = usePhase();

  // The key the routed page is mounted under. Changes only when the phase or
  // the academic year actually changes, and remounts the page when it does.
  // Same reason as the effect below for being up here with the other hooks:
  // this component early-returns three times.
  const scopeKey = useScopeKey();

  // WATCH THE SELECTED ACADEMIC YEAR, so the read-only chip beside the picker
  // appears the moment somebody switches to a finished one.
  //
  // UP HERE WITH THE OTHER HOOKS, not down beside the chip it feeds. This
  // component early-returns in three places — no user yet, no user at all, a
  // suspended school — and a hook placed after any of them runs on some renders
  // and not others, which React treats as a build error and not a warning.
  //
  // Two sources because the year can change two ways: the helper's own
  // subscription covers a change made through it, and the window event covers a
  // page that writes the selection and announces it directly.
  useEffect(() => {
    const sync = () => setViewingPastYear(isPastAcademicYear());
    sync();
    const off = onAcademicYearChanged(sync);
    window.addEventListener('app:ay:changed', sync);
    return () => {
      try { off?.(); } catch (_) { /* handler already detached */ }
      window.removeEventListener('app:ay:changed', sync);
    };
  }, []);

  // Derive extra privileges from user assignments so menu works even if backend omits them
  // The server's page list, from the shared provider so the ROUTER sees the
  // same answer. When only this layout had it, a granted page appeared in the
  // sidebar and PermissionRoute then bounced the click to the landing page.
  const { grantedByServer } = useAccess();


  // NOTE: must be declared before any early returns to satisfy React hooks rules
  // WHO HOLDS WHICH RESPONSIBILITY, AND IN WHICH PHASE.
  //
  // The phases are not symmetrical and the app's vocabulary follows the
  // school's:
  //
  //   nur_pri   CLASS TEACHER — Class.classMasterId, and they teach and mark
  //             the whole class. No departments exist here, so no head of
  //             department and no "class master".
  //   sec       CLASS MASTER — the same column, a different job: they run the
  //             class without teaching it all. HEAD OF DEPARTMENT lives here
  //             too, and only here.
  //
  // I broke this by removing the secondary gate on SubjectHead, on my own
  // reasoning that a department is scoped to a department so a nursery head
  // would hold one as well. Nursery and primary have no departments. Four
  // separate places in this codebase said SEC PHASE ONLY and I overrode all
  // four.
  //
  // This block WIDENS what the server derived and must never narrow it, which
  // is a separate rule and still holds: the three head-of-department lists are
  // all consulted, because a head appointed against a Department row has only
  // departmentHeadOf populated and checking subjectHeadOf alone missed the
  // commonest case.
  const userPrivilegesArr = useMemo(() => {
    const base = Array.isArray(user?.privileges) ? user.privileges : [];
    const extra = [];
    const phaseCode = String(activePhase || '').toLowerCase();
    const isSecPhase = phaseCode === 'sec';
    const isNurPriPhase = phaseCode === 'nur_pri';

    // A CLASS TEACHER IS A CLASS TEACHER AT EVERY LEVEL.
    //
    // This used to require a PRIMARY class in nursery/primary, so somebody
    // running a nursery class held the appointment and saw none of its pages.
    // Blaise confirmed that is wrong: nursery has class teachers too, and a
    // nursery class teacher teaches and marks the whole class exactly as a
    // primary one does.
    //
    // Both phases now say the same thing — hold a class, get the class pages —
    // and only the WORDING differs ("class teacher" here, "class master" in
    // secondary), which is a label, not a permission.
    if (Array.isArray(user?.classMasterOf) && user.classMasterOf.length > 0
      && (isSecPhase || isNurPriPhase)) {
      extra.push('ClassMaster');
    }

    // SECONDARY ONLY, across all three lists the server may populate.
    const headsSomething = (list) => Array.isArray(list) && list.length > 0;
    if (isSecPhase
      && (headsSomething(user?.subjectHeadOf)
        || headsSomething(user?.subjectGroupsHeaded)
        || headsSomething(user?.departmentHeadOf))) {
      extra.push('SubjectHead');
    }

    return Array.from(new Set([...base, ...extra]));
    // `teacherClasses` is no longer read here: it was only needed to find out
    // whether a nur_pri class was PRIMARY, and that distinction is gone —
    // nursery class teachers are class teachers too.
  }, [user, activePhase]);

  const isSuperAdminRoute = (() => {
    const p = String(location?.pathname || '');
    return p.startsWith('/super-admin') || p.startsWith('/admin') || p.startsWith('/system-admin');
  })();

  const sidebarOffset = isMobile ? 0 : (sidebarExpanded ? drawerExpandedWidth : drawerCollapsedWidth);
  // `platformNameColor` is deliberately not taken: the sidebar is the only
  // place in this layout that renders the platform name, and its ground is now
  // indigo. A colour chosen against a white page belongs on the landing and
  // login screens, which still honour it.
  const { platformName, platformLogoUrl, logoSize, platformNameFont, platformNameFontSize, platformNameFontWeight, brandingDisplay, brandingGap } = usePlatform();
  const navigate = useNavigate();

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) setSidebarExpanded(false);
  }, [isMobile]);
  const _API_BASE = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    'http://localhost:5000'
  );
  const resolvedPlatformLogo = platformLogoUrl
    ? (platformLogoUrl.startsWith('http') ? platformLogoUrl : `${_API_BASE.replace(/\/$/, '')}${platformLogoUrl}`)
    : '/encatarlogo.png';

  const getFirstAccessiblePath = (items = []) => {
    if (!Array.isArray(items)) return null;
    for (const item of items) {
      if (!item) continue;
      if (item.path) return item.path;
      if (Array.isArray(item.subMenus)) {
        for (const sub of item.subMenus) {
          if (sub?.path) return sub.path;
        }
      }
    }
    return null;
  };

  // Root landing: redirect '/' to the first accessible page in the user's menu.
  // This avoids showing an "Access Denied" screen at '/' after login.
  /* Removed auto-redirect from DashboardLayout to allow LandingPage to own the root route (/) */
  /*
  useEffect(() => {
    try {
      if (!user || user === undefined || user === null || !user.role) return;
      const currentPath = (location?.pathname || '').toString();
      if (currentPath !== '/') return;

      const userRolesArr = user && user.role ? [user.role] : [];
      const privileges = user && Array.isArray(user.privileges) ? user.privileges : [];

      const resolvedActivePhase = (() => {
        const fromCtx = (activePhase || '').toString().trim().toLowerCase();
        if (fromCtx) return fromCtx;
        const fromStorage = (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '')
          .toString()
          .trim()
          .toLowerCase();
        return fromStorage;
      })();

      const isPhaseAllowed = (phase) => {
        if (!phase || phase === 'all') return true;
        if (!resolvedActivePhase) return true;
        if (Array.isArray(phase)) return phase.includes(resolvedActivePhase);
        return String(phase).toLowerCase() === resolvedActivePhase;
      };

      // The nursery-only class teacher used to have the discipline pages
      // stripped here. Removed: a nursery class teacher is a class teacher and
      // gets the same pages as a primary one — see the note on
      // userPrivilegesArr. The two copies of that rule in this file also
      // disagreed with each other about whether class results went with it.

      const baseMenuItems = roleMenus.generateMenu(user.role, { phase: resolvedActivePhase, privileges }) || [];
      const filtered = (Array.isArray(baseMenuItems) ? baseMenuItems : [])
        .map((item) => {
          const cloned = { ...item };
          if (Array.isArray(item.subMenus)) {
            cloned.subMenus = item.subMenus.filter((sub) => {
              if (!isPhaseAllowed(sub.phase)) return false;
              if (!sub.path) return true;
              return hasPageAccess(userRolesArr, sub.path, privileges);
            });
          }
          return cloned;
        })
        .filter((item) => {
          if (!isPhaseAllowed(item.phase)) return false;
          if (Array.isArray(item.subMenus) && item.subMenus.length > 0) return true;
          if (item.path) return hasPageAccess(userRolesArr, item.path, privileges);
          return false;
        });

      const firstPath = getFirstAccessiblePath(filtered);
      if (firstPath) {
        localStorage.setItem('firstAccessiblePath', firstPath);
        if (currentPath === '/') {
          navigate(firstPath, { replace: true });
        }
      }
    } catch (_) {}
  }, [user, location.pathname, navigate, activePhase, teacherClasses]);
  */

  const isSuspendedUser = (() => {
    if (!user) return false;
    const roleNorm = (user?.role || '').toString();
    const isSystemAdmin = roleNorm === 'SystemAdmin' || roleNorm === 'SuperAdmin';
    return !isSystemAdmin && (user?.school?.status === 'suspended');
  })();

  useEffect(() => {
    if (!user) {
      setTeacherClasses(null);
      return;
    }
    if (isSuspendedUser) return;

    // FETCHED FOR ANY TEACHER, not only one already known to be a class master.
    //
    // This used to require userPrivilegesArr.includes('ClassMaster') before it
    // would ask for the classes — and in nursery/primary, userPrivilegesArr
    // derives ClassMaster FROM this very list. Neither could go first, so the
    // list stayed null, the privilege was never derived, and the "Class master"
    // group and its pages were absent for exactly the phase the class-teacher
    // rule is about. The only escape was the server's own privileges already
    // carrying it, which made the bug look intermittent.
    //
    // Gating an input on its own output is the whole defect. The list is cheap,
    // it is scoped by the server anyway, and a teacher who runs no class simply
    // gets [].
    const isTeacher = ['Teacher', 'ClassMaster'].includes((user?.role || '').toString());

    if (!isTeacher) {
      setTeacherClasses(null);
      return;
    }

    (async () => {
      try {
        const classes = await fetchJson('/api/teacher/classes');
        setTeacherClasses(Array.isArray(classes) ? classes : []);
      } catch (_) {
        setTeacherClasses([]);
      }
    })();
  }, [user, isSuspendedUser]);

  // Derived state used by effects below
  const msgOpen = Boolean(msgAnchor);

  const closeHeaderMenus = () => {
    setNotifAnchor(null);
    setMsgAnchor(null);
    setProfileAnchor(null);
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleNavigateFromSidebar = (path) => {
    if (!path) return;
    if (isMobile) {
      setMobileOpen(false);
    }
    navigate(path);
  };

  // Toggle sidebar expansion
  const toggleSidebar = () => {
    if (isMobile) {
      closeHeaderMenus();
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarExpanded(!sidebarExpanded);
    }
  };

  // Close mobile drawer on route change
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Clean up orphaned MUI backdrop/modal portals on route change to prevent dark overlay lock
  useEffect(() => {
    try {
      setNotifAnchor(null);
      setMsgAnchor(null);
      setProfileAnchor(null);
      // Remove any backdrop left behind by the previous route's dialogs/menus.
      releaseStuckOverlays();
    } catch (_) {}
  }, [location.pathname]);

  // Watchdog: continuously clear any orphaned dark overlay (a backdrop with no
  // dialog/menu on top of it) that would otherwise lock the whole UI. Only removes
  // truly orphaned overlays, so genuinely-open dialogs/menus are left untouched.
  useEffect(() => {
    const stop = startOverlayWatchdog(1000);
    return stop;
  }, []);

  // Redirect to login automatically when a 401 is detected by fetchJson
  useEffect(() => {
    const handler = () => {
      if (location.pathname !== '/login') {
        // Close any open dialog/menus first so unmounting this layout can't orphan a backdrop.
        try {
          setSubscriptionOpen(false);
          setNotifAnchor(null);
          setMsgAnchor(null);
          setProfileAnchor(null);
        } catch (_) {}
        // Non-force: only clears already-orphaned backdrops. The layout is still
        // mounted here, so force-removing a live dialog node could make React throw
        // on unmount; the login page force-cleans any orphan after this unmounts.
        releaseStuckOverlays();
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('app:auth:required', handler);
    return () => window.removeEventListener('app:auth:required', handler);
  }, [navigate, location.pathname]);

  // Move useEffect above authentication checks to avoid conditional hook call
  useEffect(() => {
    if (user !== undefined && user !== null) {
      if (!isSuspendedUser) {
        loadNotifications();
        // initial messages counters
        loadMsgUnreadCount();
        // Register socket for live updates
        const uid = user?.id || user?.userId || user?.uid || null;
        if (uid) {
          registerCurrentUser(uid);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuspendedUser]);

  // Session timeout & token auto-refresh are now handled by useSessionManager hook

  // Listen for school branding updates (logo upload from SchoolSettingsPage)
  useEffect(() => {
    const handler = (e) => {
      const { logoUrl, name } = e?.detail || {};
      setSchoolInfo((prev) => ({
        ...(prev || {}),
        ...(logoUrl !== undefined ? { logoUrl } : {}),
        ...(name !== undefined ? { name } : {}),
      }));
    };
    window.addEventListener('school:branding:updated', handler);
    return () => window.removeEventListener('school:branding:updated', handler);
  }, []);

  // Load minimal school info (name, logo) for branding in AppBar
  useEffect(() => {
    if (!user) {
      setSchoolInfo(null);
      return;
    }
    if (isSuspendedUser) {
      setSchoolInfo(null);
      return;
    }
    const roleNorm = (user?.role || '').toString();
    const isSystemAdmin = roleNorm === 'SystemAdmin' || roleNorm === 'SuperAdmin';
    if (isSystemAdmin || !user?.schoolId) {
      setSchoolInfo(null);
      return;
    }

    // Default for ALL school users: use /api/users/me's embedded school
    try {
      const meSchool = user?.school;
      if (meSchool && (meSchool.name || meSchool.logoUrl)) {
        setSchoolInfo({ name: meSchool.name || null, logoUrl: meSchool.logoUrl || null });
      } else {
        setSchoolInfo(null);
      }
    } catch (_) {
      setSchoolInfo(null);
    }

    // If logo is missing (common for non-admin roles), fetch minimal school branding.
    (async () => {
      try {
        const resp = await fetchJson('/api/schools/me', { method: 'GET' });
        const s = resp?.school || resp || {};
        if (s && (s.name || s.logoUrl)) {
          setSchoolInfo((prev) => ({
            name: s.name || prev?.name || null,
            logoUrl: s.logoUrl || prev?.logoUrl || null
          }));
        }
      } catch (_) {}
    })();

    // Only SchoolAdmin/roles with access to settings page should fetch detailed school settings
    const privileges = user && Array.isArray(user.privileges) ? user.privileges : [];
    const canReadSchoolSettings = hasPageAccess([user.role], '/school-setup-and-configuration/school-settings', privileges);
    if (!canReadSchoolSettings) return;

    (async () => {
      try {
        const res = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        const data = (res && (res.data?.school || res.school || res)) || {};
        setSchoolInfo(data);
      } catch (_) {}
    })();
  }, [user, isSuspendedUser]);

  // Listen for live 'message:new' events
  useEffect(() => {
    let off = null;
    (async () => {
      if (isSuspendedUser) return;
      off = await onEvent('message:new', async (payload) => {
        // Bump unread count optimistically and refresh list if dropdown open
        setMsgUnread((n) => (n || 0) + 1);
        try {
          const fromName = (payload?.fromUserName || payload?.fromName || '').toString();
          const fromAvatarUrl = (payload?.fromUserAvatarUrl || payload?.fromAvatarUrl || '').toString();
          const subject = (payload?.subject || '').toString();
          const rawPreview = (payload?.content || payload?.message || '').toString();
          const preview = rawPreview.length > 90 ? `${rawPreview.slice(0, 90)}…` : rawPreview;
          setMessageSnack({ open: true, fromName, fromAvatarUrl, subject, preview });
        } catch (_) {}
        if (msgOpen) {
          await loadMsgList();
        }
      });
    })();
    return () => { if (typeof off === 'function') off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgOpen, isSuspendedUser]);

  // Listen for live 'message:read' events to sync unread badge
  useEffect(() => {
    let off = null;
    (async () => {
      if (isSuspendedUser) return;
      off = await onEvent('message:read', async (_payload) => {
        setMsgUnread((n) => Math.max(0, (n || 0) - 1));
        if (msgOpen) {
          await loadMsgList();
        }
      });
    })();
    return () => { if (typeof off === 'function') off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgOpen, isSuspendedUser]);

  // Listen for live 'announcement:new' events
  useEffect(() => {
    let off = null;
    (async () => {
      off = await onEvent('announcement:new', async (payload) => {
        const title = payload?.title || 'Announcement';
        const body = payload?.body || '';
        const createdAt = payload?.createdAt || new Date().toISOString();
        // Show snackbar
        setAnnounceSnack({ open: true, title, body });
        // Push into notifications list and bump unread count so it shows in dropdown
        setNotifications((prev) => [
          { id: payload?.id || `ANN-${Date.now()}`, type: 'announcement', title, message: body, createdAt },
          ...(Array.isArray(prev) ? prev : [])
        ]);
        setUnreadCount((n) => (n || 0) + 1);
      });
    })();
    return () => { if (typeof off === 'function') off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let off = null;
    (async () => {
      if (isSuspendedUser) return;
      off = await onEvent('new-notification', async (raw) => {
        const plain = raw && raw.dataValues ? raw.dataValues : raw;
        const payload = plain?.payload && typeof plain.payload === 'object' ? plain.payload : null;
        const read = plain?.read !== undefined ? !!plain.read : !!plain?.isRead;
        const title = plain?.title || payload?.title || null;
        const message = plain?.message || payload?.message || null;
        const createdAt = plain?.createdAt || payload?.createdAt || new Date().toISOString();
        const type = plain?.type || payload?.type || null;
        const sourceRole = payload?.sourceRole || payload?.senderRole || payload?.role || plain?.sourceRole || plain?.senderRole || plain?.role || null;
        const notif = {
          ...plain,
          read,
          title,
          message,
          createdAt,
          type,
          sourceRole: sourceRole || null
        };

        setNotifications((prev) => [notif, ...(Array.isArray(prev) ? prev : [])]);
        if (!read) {
          setUnreadCount((n) => (n || 0) + 1);
        }
        try {
          const src = payload?.sourceRole || payload?.senderRole || payload?.role || plain?.sourceRole || plain?.senderRole || plain?.role || type || '';
          setNotifSnack({
            open: true,
            title: (title || '').toString(),
            message: (message || '').toString(),
            source: src ? String(src) : ''
          });
        } catch (_) {}
        if (String(type) === 'announcement') {
          setAnnounceSnack({ open: true, title: title || 'Announcement', body: message || '' });
        }
      });
    })();
    return () => { if (typeof off === 'function') off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuspendedUser]);

  // Background polling for message unread count
  useEffect(() => {
    let timer;
    if (user && !isSuspendedUser) {
      timer = setInterval(() => {
        loadMsgUnreadCount();
      }, 30000);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [user, isSuspendedUser]);

  // While suspended, poll for status changes so the lockout is only removed after successful renewal.
  useEffect(() => {
    if (!isSuspendedUser) return;
    let timer;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const me = await fetchJson('/api/users/me', { method: 'GET' });
        const status = me?.school?.status;
        if (!cancelled && status && status !== 'suspended') {
          window.location.reload();
        }
      } catch (_) {}
    };

    checkStatus();
    timer = setInterval(checkStatus, 10000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [isSuspendedUser]);

  // Auto-load subscription (drives icon color + automatic expired/grace notice)
  useEffect(() => {
    if (!canManageSubscriptionForSubscription) return;
    if (subscriptionFetchedOnceRef.current) return;
    subscriptionFetchedOnceRef.current = true;
    loadSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSubscriptionForSubscription]);

  // Auto-open the existing subscription dialog when status is expired/grace/suspended.
  useEffect(() => {
    if (!canManageSubscriptionForSubscription) return;
    const effective = (subscriptionUi?.effectiveStatus || '').toString();
    const shouldPrompt = effective === 'expired' || effective === 'grace' || effective === 'suspended';
    if (shouldPrompt && !subscriptionOpen && !subscriptionDialogDismissedRef.current) {
      setSubscriptionOpen(true);
    }
  }, [canManageSubscriptionForSubscription, subscriptionUi?.effectiveStatus, subscriptionOpen]);

  // If subscription status changes, allow auto-prompt again (e.g., renewed -> expired again)
  useEffect(() => {
    subscriptionDialogDismissedRef.current = false;
  }, [subscriptionUi?.effectiveStatus]);

  // If API calls start failing due to subscription expiry, open the dialog and refresh subscription info.
  useEffect(() => {
    if (!canManageSubscriptionForSubscription) return;
    const onExpired = async () => {
      try {
        setSubscriptionOpen(true);
        await loadSubscription();
      } catch (_) {}
    };
    window.addEventListener('app:subscription:expired', onExpired);
    return () => window.removeEventListener('app:subscription:expired', onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSubscriptionForSubscription]);

  // Loading and authentication states
  if (user === undefined) {
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  const roleNorm = (user?.role || '').toString();
  const isSystemAdmin = roleNorm === 'SystemAdmin' || roleNorm === 'SuperAdmin';
  const isSchoolAdmin = roleNorm === 'SchoolAdmin';
  const isPrincipal = roleNorm === 'Principal';
  const isHeadMaster = roleNorm === 'HeadMaster';
  const canManageSubscription = isSchoolAdmin || isPrincipal || isHeadMaster;
  const isSchoolSuspended = !isSystemAdmin && (user?.school?.status === 'suspended');
  const suspensionReason = user?.school?.suspensionReason || null;

  const subscriptionBadge = (() => {
    try {
      if (!canManageSubscription) return false;
      const s = (subscriptionUi?.effectiveStatus || '').toString();
      if (!s) return false;
      if (s === 'suspended' || s === 'expired' || s === 'grace') return true;
      const remainingDays = subscriptionUi?.timeLeftDays;
      if (!Number.isFinite(remainingDays)) return false;
      return remainingDays <= 7;
    } catch (_) {
      return null;
    }
  })();

  const showExpiredBanner = (() => {
    if (!canManageSubscription) return false;
    const effective = (subscriptionUi?.effectiveStatus || '').toString();
    return effective === 'expired' || effective === 'grace' || effective === 'suspended';
  })();

  // GREEN active, AMBER close to expiring or in grace, RED gone.
  //
  // "Close to expiring" was missing: a subscription with three days left drew
  // the same green as one with three months, so the warning arrived only once
  // the end date had already passed. The seven-day threshold is the one the
  // banner text below already uses — this makes the light agree with the words.
  const subscriptionIconColor = (() => {
    if (!canManageSubscription) return 'inherit';
    if (!subscription) return 'inherit';
    const s = (subscriptionUi?.effectiveStatus || '').toString();
    const daysLeft = subscriptionUi?.timeLeftDays;
    if (s === 'expired' || s === 'suspended') return theme.palette.error.main;
    if (s === 'grace') return theme.palette.warning.main;
    if (s === 'active' || s === 'trial') {
      if (Number.isFinite(daysLeft) && daysLeft <= 7) return theme.palette.warning.main;
      return theme.palette.success.main;
    }
    return 'inherit';
  })();

  // EVERY SIGNED-IN USER SEES THE YEAR, as the phone has done all along.
  //
  // This was an allow-list of four role spellings, so a teacher, a bursar or a
  // parent had no way to tell WHICH academic year they were looking at, let
  // alone change it — while every request they made carried a year header they
  // could not see. A teacher opening last year's marks and one opening this
  // year's had identical screens.
  //
  // The year is not privileged information: it is the context everything else on
  // the page is scoped to, and a past year is read-only for everybody by the
  // same server rule. The PHASE switcher beside it stays admin-only, because
  // that genuinely does change what a person may reach.
  // EVERY SCHOOL PORTAL, and this time the mechanism behind the last attempt is
  // fixed rather than guessed at.
  //
  // The first time this opened up, the class-master and head-of-department pages
  // stopped working and it was reverted, because the cause could not be found by
  // reading. It has been found now, and the reasoning in that revert was right
  // about where to look: YearSelector does not merely display a year, it
  // RESOLVES one and writes it to storage on mount, after which every request
  // that user makes carries X-Academic-Year-Id.
  //
  // The server reads that header into req.academicYearId, and several endpoints
  // apply it as an equality filter ONLY WHEN IT IS PRESENT. So a portal with no
  // switcher sent no header and got no filter; adding the switcher switched the
  // filter on. Rows written before academicYearId was populated hold NULL there
  // and an equality test excludes them, so a teacher whose data had not changed
  // at all saw their subject-head pages come back empty.
  //
  // MarkController.getMarks and teacherController.getMyClasses already guarded
  // against this — they drop the year and retry when a query comes back empty,
  // which is exactly why the marks screen and the class list survived while the
  // subject-head endpoints did not. Those endpoints have the same ladder now
  // (/api/subject-head/classes, /evaluations, /monthly-reports and the
  // performance query), so the header can no longer empty a page on its own.
  //
  // The year is not privileged information: it is the context everything else on
  // the page is scoped to, and a past year is read-only for everybody by the
  // same server rule. The PHASE switcher beside it stays admin-only, because
  // that genuinely does change what a person may reach.
  //
  // System-level roles are excluded: they are not scoped to one school's
  // sessions, so a school academic year means nothing to them.
  const yearBarVisible = (() => {
    const rn = (user?.role || '').toString().toLowerCase().replace(/\s+/g, '');
    if (!rn) return false;
    const systemRoles = new Set(['systemadmin', 'itmanager', 'salesrep', 'supportagent']);
    return !systemRoles.has(rn);
  })();

  // WHICH OF THE TWO PLACES THE SWITCHER LIVES IN — decided in JS, not CSS.
  //
  // It was `display: { xs: 'none', md: 'flex' }` on one copy and the inverse on
  // the other, which hides a component but does NOT unmount it. Both copies
  // were therefore alive at every width: two mounts, two fetches of the year
  // catalogue, and two racing writes of the academic year on every page load,
  // one of which could resolve to a different answer than the other.
  //
  // A media query mounts exactly one — and it is declared far above, with the
  // other hooks, because there are early returns between here and there.

  const resolvedActivePhase = (() => {
    const fromCtx = (activePhase || '').toString().trim().toLowerCase();
    if (fromCtx) return fromCtx;
    const fromStorage = (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
    return fromStorage;
  })();

  const isPhaseAllowed = (phase) => {
    if (!phase || phase === 'all') return true;
    if (!resolvedActivePhase) return true;
    if (Array.isArray(phase)) return phase.includes(resolvedActivePhase);
    return String(phase).toLowerCase() === resolvedActivePhase;
  };

  const getMenuIconColor = (textKey) => {
    const k = (textKey || '').toString();
    if (k === 'Dashboard') return '#3B82F6';
    if (k === 'Class Management') return '#C7D2FE';
    if (k === 'Staff Management') return '#A78BFA';
    if (k === 'Subject Management') return '#0EA5E9';
    if (k === 'Student Management') return '#16A34A';
    if (k === 'Academics & Exams') return '#0891B2';
    // The teacher portal was one "Teaching & Learning" group; it is now three,
    // matching the mobile drawer. Kept here (harmless if unused) so a school
    // still on the old grouping does not lose its colour, with the replacements
    // sharing the same indigo family so the section still reads as one area.
    if (k === 'Teaching & Learning') return '#A5B4FC';
    if (k === 'Academics') return '#A5B4FC';
    // Both names for the same group — nursery/primary calls them a class
    // teacher, secondary a class master — so the colour must answer to either or
    // the heading loses it in one phase.
    if (k === 'Class master' || k === 'Class teacher') return '#C7D2FE';
    if (k === 'Head of department') return '#818CF8';
    if (k === 'Finance') return '#F59E0B';
    if (k === 'Facilities') return '#14B8A6';
    if (k === 'Communication') return '#EC4899';
    if (k === 'Support & Maintenance') return '#EF4444';
    if (k === 'School Setup & Configuration') return '#94A3B8';
    // THE OTHER PORTALS. System admin, parent and teacher entries had no
    // colour at all and fell through to a grey — the school-admin sidebar was
    // a row of coloured markers and every other portal a uniform list.
    if (k === 'Overview') return '#3B82F6';
    if (k === 'Schools') return '#0EA5E9';
    if (k === 'Billing & Subscriptions') return '#F59E0B';
    if (k === 'Users & Access') return '#A78BFA';
    if (k === 'Platform Config') return '#94A3B8';
    if (k === 'Security & Audit') return '#EF4444';
    if (k === 'Support Tickets') return '#EF4444';
    if (k === 'IT Services') return '#0891B2';
    if (k === 'Children & Academics') return '#16A34A';
    if (k === 'Attendance Records') return '#0891B2';
    if (k === 'Fees & Payments') return '#F59E0B';
    if (k === 'Messages') return '#EC4899';
    if (k === 'Enter marks') return '#A5B4FC';
    if (k === 'Take register') return '#0891B2';
    if (k === 'Class results') return '#C7D2FE';
    if (k === 'Class discipline') return '#EF4444';
    if (k === 'Subject report') return '#0EA5E9';
    if (k === 'Departmental report') return '#818CF8';
    return theme.palette.text.secondary;
  };

  // `isNurseryClassMaster` was defined here and stripped the discipline AND
  // class-results pages from a nursery-only class teacher. Removed: a nursery
  // class teacher is a class teacher and gets the same pages as a primary one.
  // Note the other copy of this rule, ~600 lines above, stripped a DIFFERENT set
  // of paths — the two never agreed, which is its own argument against having
  // had it twice.

  const systemPermsArr = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
  const roleSlugForSystemPerms = (user?.role || '').toString().toLowerCase().replace(/\s+/g, '');
  const isSystemUserRoleForPerms = ['systemadmin', 'itmanager', 'salesrep', 'supportagent'].includes(roleSlugForSystemPerms) && (user?.schoolId == null);

  const mapSuperAdminPathToPermKey = (path) => {
    if (!path) return null;
    const p = String(path).split('#')[0].split('?')[0];
    if (!p.startsWith('/super-admin/')) return null;
    if (p.startsWith('/super-admin/system-overview')) return 'system-overview';
    if (p.startsWith('/super-admin/schools')) return 'schools';
    if (p.startsWith('/super-admin/registration-queue')) return 'registration-queue';
    if (p.startsWith('/super-admin/onboarding-status')) return 'onboarding-status';
    if (p.startsWith('/super-admin/billing')) return 'billing';
    if (p.startsWith('/super-admin/users-access')) return 'users-access';
    if (p.startsWith('/super-admin/system-settings')) return 'system-settings';
    if (p.startsWith('/super-admin/communications')) return 'communications';
    if (p.startsWith('/super-admin/security')) return 'security';
    if (p.startsWith('/super-admin/support-tickets')) return 'support-tickets';
    if (p.startsWith('/super-admin/integrations')) return 'integrations';
    if (p.startsWith('/super-admin/it/')) return 'it-services';
    return null;
  };

  const hasSystemPortalAccess = (path) => {
    if (!isSystemUserRoleForPerms) return true;
    if (!systemPermsArr) return true;
    if (systemPermsArr.includes('*')) return true;
    const key = mapSuperAdminPathToPermKey(path);
    if (!key) return true;
    return systemPermsArr.includes(key);
  };

  // Generate dynamic menu based on user's role
  const userRolesArr = user && user.role ? [user.role] : [];
  // MAY THIS PERSON OPEN THIS PAGE — the server's answer OR the local rules.
  //
  // A union, deliberately. The server is the authority (it is what the phone
  // uses and it is what actually enforces the request), but a union means a
  // failed or slow /my-access cannot hide anything that works today. It also
  // means the local re-derivation no longer has to be perfect for a teacher to
  // reach their own pages.
  const mayAccess = (path) => {
    if (!path) return false;
    if (grantedByServer(path)) return true;
    return hasPageAccess(userRolesArr, path, userPrivilegesArr);
  };

  /**
   * A DECLARED PHASE MISMATCH HIDES THE ITEM, even when the server granted it.
   *
   * This used to be `!grantedByServer(path) && !isPhaseAllowed(phase)` — the
   * server's grant won outright, so `phase: 'sec'` on the Head of department
   * entries did nothing whenever /api/permissions/my-access listed them. And it
   * lists them more often than it should: AccessContext fetches that list
   * EARLY, and if PhaseContext has not yet written schoolAdmin_activePhase the
   * request carries no X-Active-Phase for the server to filter on. The answer
   * is then cached for the whole session, so the pages stay in the menu no
   * matter what the phase resolves to a moment later.
   *
   * The original reasoning still holds and is kept: an UNRESOLVED phase must not
   * hide pages the server is granting — that is the fault the union was added
   * for, and it cost real teachers their own pages. So the phase only overrules
   * a grant once it is actually known.
   *
   * A page that declares `phase: 'sec'` is wrapped in SecPhaseRoute and its
   * endpoints 403 outside secondary, so showing it in nursery/primary can only
   * produce a menu entry that fails when clicked.
   */
  const phaseRefuses = (node) => {
    if (!node?.phase) return false;
    if (resolvedActivePhase) return !isPhaseAllowed(node.phase);
    return !grantedByServer(node.path) && !isPhaseAllowed(node.phase);
  };

  const baseMenuItems = user
    ? roleMenus.generateMenu(user.role, { phase: resolvedActivePhase, privileges: userPrivilegesArr, allowPath: mayAccess }) || []
    : [];
  // Determine if this user's role supports collapsible dropdown submenus
  const _roleNormForMenuCompute = (user?.roleNormalized || user?.role || '').toLowerCase().replace(/\s+/g, '');
  const _roleAllowsDropdowns = ['schooladmin','principal','headmaster','principalheadteacher','systemadmin','superadmin'].includes(_roleNormForMenuCompute);

  // Ensure menu only shows pages the user has page access to
  const menuItems = baseMenuItems.map((item) => {
    const cloned = { ...item };
    if (Array.isArray(item.subMenus)) {
      cloned.subMenus = item.subMenus.filter((sub) => {
        if (phaseRefuses(sub)) return false;
        if (!sub.path) return true;
        return mayAccess(sub.path) && hasSystemPortalAccess(sub.path);
      });
    }
    return cloned;
  }).filter((item) => {
    if (phaseRefuses(item)) return false;
    if (Array.isArray(item.subMenus) && item.subMenus.length > 0) return true;
    if (item.path) return mayAccess(item.path) && hasSystemPortalAccess(item.path);
    return false;
  }).reduce((acc, item) => {
    // For roles without dropdown support, flatten subMenu groups into top-level items
    if (!_roleAllowsDropdowns && !item.path && Array.isArray(item.subMenus) && item.subMenus.length > 0) {
      item.subMenus.forEach(sub => acc.push(sub));
    } else {
      acc.push(item);
    }
    return acc;
  }, []);

  // Check if user has access to current page
  const canAccessCurrentPage = () => {
    if (!user || !user.role) return false;
    const currentPath = location.pathname;
    if (currentPath === '/') {
      const firstPath = getFirstAccessiblePath(menuItems);
      return !!firstPath;
    }
    return mayAccess(currentPath) && hasSystemPortalAccess(currentPath);
  };

  // Fetch notifications for the current user
  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const data = await fetchJson("/api/notifications");
      const notificationList = Array.isArray(data?.notifications) ? data.notifications : [];
      setNotifications(notificationList);
      setUnreadCount(notificationList.filter(n => !n.read).length);
    } catch (e) {
      setNotifications([]);
      setUnreadCount(0);
      // Optionally handle error
    } finally {
      setNotifLoading(false);
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await fetchJson(`/api/notifications/${notifId}/read`, { method: "PUT" });
      loadNotifications();
    } catch (e) {}
  };

  // Messages helpers
  const loadMsgUnreadCount = async () => {
    try {
      const data = await fetchJson('/api/messages/unread-count');
      setMsgUnread(Number(data?.count || 0));
    } catch (_) {
      setMsgUnread(0);
    }
  };

  const loadMsgList = async () => {
    setMsgLoading(true);
    try {
      const data = await fetchJson('/api/messages?type=inbox&limit=50');
      setMsgList(Array.isArray(data?.messages) ? data.messages : []);
    } catch (_) {
      setMsgList([]);
    } finally {
      setMsgLoading(false);
    }
  };

  // Notification menu handlers
  const notifOpen = Boolean(notifAnchor);
  const handleNotifClick = (e) => {
    const anchor = e.currentTarget;
    if (notifOpen) {
      setNotifAnchor(null);
      return;
    }
    if (isMobile) setMobileOpen(false);
    setMsgAnchor(null);
    setProfileAnchor(null);
    setNotifAnchor(anchor);
    loadNotifications();
  };
  const handleNotifClose = () => setNotifAnchor(null);

  // Messages menu handlers
  const handleMsgClick = async (e) => {
    const anchor = e.currentTarget;
    if (msgOpen) {
      setMsgAnchor(null);
      return;
    }
    if (isMobile) setMobileOpen(false);
    setNotifAnchor(null);
    setProfileAnchor(null);
    setMsgAnchor(anchor);
    await loadMsgList();
  };
  const handleMsgClose = () => setMsgAnchor(null);

  // Profile menu handlers
  const profileOpen = Boolean(profileAnchor);
  const handleProfileClick = (e) => {
    const anchor = e.currentTarget;
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch (_) {}
    if (profileOpen) {
      setProfileAnchor(null);
      return;
    }
    if (isMobile) setMobileOpen(false);
    setNotifAnchor(null);
    setMsgAnchor(null);
    setProfileAnchor(anchor);
  };
  const handleProfileClose = () => setProfileAnchor(null);

  const isImpersonating = !!localStorage.getItem('impersonationToken');

  const handleStopImpersonation = () => {
    let restored = false;
    try {
      const original =
        localStorage.getItem('impersonatorAccessToken')
        || localStorage.getItem('impersonator_accessToken')
        || localStorage.getItem('impersonatorToken')
        || sessionStorage.getItem('impersonatorAccessToken')
        || sessionStorage.getItem('impersonator_accessToken')
        || sessionStorage.getItem('impersonatorToken');
      if (original) {
        localStorage.setItem('accessToken', original);
        try { localStorage.setItem('token', original); } catch (_) {}
        restored = true;
      }

      const prevActiveSchoolId = localStorage.getItem('impersonatorActiveSchoolId');
      const prevScopeSchoolId = localStorage.getItem('impersonatorScopeSchoolId');
      if (prevActiveSchoolId != null) {
        localStorage.setItem('activeSchoolId', prevActiveSchoolId);
      }
      if (prevScopeSchoolId != null) {
        localStorage.setItem('scopeSchoolId', prevScopeSchoolId);
      }

      localStorage.removeItem('impersonationToken');
      localStorage.removeItem('impersonatorAccessToken');
      localStorage.removeItem('impersonatorActiveSchoolId');
      localStorage.removeItem('impersonatorScopeSchoolId');
      sessionStorage.removeItem('impersonatorAccessToken');
      sessionStorage.removeItem('impersonator_accessToken');
      sessionStorage.removeItem('impersonatorToken');

      // Force app to re-fetch the correct user after leaving impersonation
      try { localStorage.removeItem('user'); } catch (_) {}
      try { window.dispatchEvent(new Event('app:auth:changed')); } catch (_) {}
    } finally {
      handleProfileClose();
      try {
        localStorage.setItem('superAdminSchoolsActiveTab', '1');
      } catch (_) {}
      window.location.href = '/super-admin/schools';
    }
  };

  const handleLogout = () => {
    // Drop cached API responses first: on a shared school device, student PII must
    // not survive logout into the next user's session.
    try { purgeOfflineCaches(); } catch (_) {}
    try { localStorage.removeItem('accessToken'); } catch (_) {}
    try { localStorage.removeItem('token'); } catch (_) {}
    try { localStorage.removeItem('refreshToken'); } catch (_) {}
    try { localStorage.removeItem('user'); } catch (_) {}
    try { localStorage.removeItem('schoolId'); } catch (_) {}
    try { localStorage.removeItem('activeSchoolId'); } catch (_) {}
    try { localStorage.removeItem('scopeSchoolId'); } catch (_) {}
    try { localStorage.removeItem('impersonationToken'); } catch (_) {}
    try { localStorage.removeItem('impersonatorAccessToken'); } catch (_) {}
    try { localStorage.removeItem('impersonatorActiveSchoolId'); } catch (_) {}
    try { localStorage.removeItem('impersonatorScopeSchoolId'); } catch (_) {}

    try { sessionStorage.removeItem('impersonatorAccessToken'); } catch (_) {}
    try { sessionStorage.removeItem('impersonator_accessToken'); } catch (_) {}
    try { sessionStorage.removeItem('impersonatorToken'); } catch (_) {}

    try {
      window.dispatchEvent(new Event('app:auth:changed'));
    } catch (_) {}

    // Close any open dialog/menus and clear already-orphaned overlays. Use non-force
    // (this layout is still mounted): force-removing a live dialog node could make
    // React throw on unmount. The login page force-cleans any orphan afterwards.
    try {
      setSubscriptionOpen(false);
      setNotifAnchor(null);
      setMsgAnchor(null);
      setProfileAnchor(null);
    } catch (_) {}
    releaseStuckOverlays();

    navigate('/login', { replace: true });
  };

  function toDisplayText(value, fallback = '') {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') return String(value);
    if (typeof value === 'boolean') return value ? 'true' : fallback;
    if (Array.isArray(value)) {
      const text = value.map((entry) => toDisplayText(entry, '')).filter(Boolean).join(', ');
      return text || fallback;
    }
    if (typeof value === 'object') {
      const candidates = [value.title, value.name, value.label, value.text, value.message, value.value];
      for (const candidate of candidates) {
        const text = toDisplayText(candidate, '');
        if (text) return text;
      }
      try {
        const text = JSON.stringify(value);
        return text && text !== '{}' ? text : fallback;
      } catch (_) {
        return fallback;
      }
    }
    return fallback;
  }

  // Helper to get initials from name
  function getInitials(name) {
    const safeName = toDisplayText(name, '').trim();
    if (!safeName) return '?';
    const parts = safeName.split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000')
    .replace(/\/$/, '')
    .replace(/\/api$/i, '');
  const avatarSrc = user?.avatarUrl
    ? (user.avatarUrl.startsWith('http')
        ? user.avatarUrl
        : `${apiBase}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`)
    : undefined;

  const resolveMediaUrl = (rawUrl) => {
    const u = (rawUrl || '').toString().trim();
    if (!u) return undefined;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return `${apiBase}${u.startsWith('/') ? '' : '/'}${u}`;
  };

  const schoolLogoSrc = schoolInfo?.logoUrl ? resolveMediaUrl(schoolInfo.logoUrl) : undefined;
  const mobileAppBarLogoSrc = schoolLogoSrc || (brandingDisplay !== 'nameOnly' ? resolvedPlatformLogo : undefined);
  const mobileAppBarLogoRightInset = canManageSubscription ? 200 : 152;
  const safeSchoolName = toDisplayText(schoolInfo?.name, '');
  const safePlatformName = toDisplayText(platformName, toDisplayText(t("appName"), 'App'));
  const safeUserName = toDisplayText(user?.name, '');
  const safeUserEmail = toDisplayText(user?.email || user?.username, '');
  const safeUserRoleLabel = toDisplayText(t(user?.role), toDisplayText(user?.role, ''));

  // State to track which submenus are open
  const handleToggleMenu = (idx) => {
    setOpenMenus((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (isSchoolSuspended) {
    return (
      <Box sx={{ height: '100vh', width: '100vw', bgcolor: 'background.default' }}>
        <CssBaseline />
        <Dialog
          open
          fullScreen
          disableEscapeKeyDown
          onClose={(_e, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          }}
        >
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Your school account has been suspended.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Access to the system is temporarily disabled.
                {suspensionReason ? ` Reason: ${suspensionReason}` : ''}
              </Typography>
              {!isSchoolAdmin && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Please contact your School Administrator to renew the subscription.
                </Typography>
              )}
            </Alert>

            {isSchoolAdmin && (
              <Box sx={{ mt: 2 }}>
                <SubscriptionRenewalPage />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            {isImpersonating && (
              <Button variant="outlined" onClick={handleStopImpersonation}>
                Stop Impersonation
              </Button>
            )}
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Refresh Access
            </Button>
            <Button variant="contained" color="error" onClick={handleLogout}>
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // True when sidebar is showing full content (desktop expanded, or mobile temp drawer)
  const isDrawerExpanded = isMobile || sidebarExpanded;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      {/* AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: isMobile ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
          width: { xs: '100%', sm: `calc(100% - ${sidebarOffset}px)` },
          ml: { xs: 0, sm: `${sidebarOffset}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),

          // THE TOP BAR IS THE SIDEBAR'S COLOUR, closed by the same gold rule.
          // Left on the default paper it was a white strip sitting on top of an
          // indigo sidebar — two different products meeting at a corner. Indigo
          // across both makes the chrome one shape, and the content area the
          // only thing on the page that is light.
          // A FLAT FILL, not a gradient. A gradient means the bar has no single
          // colour — only its first column of pixels is SIDEBAR.bg and the rest
          // is a blend, so a colour picker anywhere on it reports something
          // other than the value that was set. That cost several rounds of
          // "this is not the colour I gave you", and it was right every time.
          backgroundColor: SIDEBAR.bg,
          color: SIDEBAR.text,
          borderBottom: `3px solid ${SIDEBAR.accent}`,
          backdropFilter: 'none',

          // Everything in the bar inherits the light foreground rather than the
          // page's ink, which would be near-black on indigo.
          '& .MuiIconButton-root': { color: SIDEBAR.icon },
          '& .MuiIconButton-root:hover': {
            color: '#FFFFFF', backgroundColor: SIDEBAR.hover,
          },
          '& .MuiTypography-root': { color: SIDEBAR.text },
          // A TRANSLUCENT chip, matching the mobile drawer's year chip exactly.
          //
          // This borrowed SIDEBAR.active — the MENU's focus colour — which only
          // ever worked by coincidence, while that focus was gold and happened
          // to carry white text. The focus band is ivory now, so the same
          // borrowing would have put white text on ivory at 1.06:1: invisible.
          // A chip in the top bar has nothing to do with which menu item is
          // selected, so it no longer takes its colour from one.
          '& .MuiChip-root': {
            backgroundColor: 'rgba(255,255,255,0.14)',
            color: SIDEBAR.text,
            borderColor: SIDEBAR.line,
          },
        }}
      >
        <Toolbar sx={{ position: 'relative' }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleSidebar}
            sx={{ mr: 2, flexShrink: 0, zIndex: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Centered school branding: hidden on mobile, visible on sm+ */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            {!isSystemAdmin && (
              <React.Fragment>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 1,
                    color: 'inherit',
                    textDecoration: 'none'
                  }}
                >
                  {schoolLogoSrc && (
                    <Box
                      component="img"
                      src={schoolLogoSrc}
                      alt={safeSchoolName || 'School Logo'}
                      sx={{ height: 36, maxHeight: 40, objectFit: 'contain', borderRadius: 1, mr: 0.5 }}
                    />
                  )}
                  {safeSchoolName && (
                    <Typography
                      variant="h6"
                      noWrap
                      sx={{ fontWeight: 700, letterSpacing: 0.5, maxWidth: { sm: 200, md: 'none' } }}
                    >
                      {safeSchoolName}
                    </Typography>
                  )}
                </Box>
                {mobileAppBarLogoSrc && (
                  <Box
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      position: 'absolute',
                      left: 60,
                      right: `${mobileAppBarLogoRightInset}px`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      px: 1,
                      zIndex: 1
                    }}
                  >
                    <Box
                      component="img"
                      src={mobileAppBarLogoSrc}
                      alt={safeSchoolName || safePlatformName}
                      sx={{ height: 30, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
                    />
                  </Box>
                )}
              </React.Fragment>
            )}
          </Box>

          {/* Removed YearSelector from AppBar to avoid congestion */}

          {/* Phase Switcher moved to the Academic Year bar below */}

          {/* Subscription */}
          {canManageSubscription && (
            <Tooltip title="Subscription">
              <IconButton color="inherit" onClick={handleOpenSubscription} sx={{ mr: 2, zIndex: 2 }}>
                <Badge variant="dot" color="error" overlap="circular" invisible={!subscriptionBadge}>
                  <WorkspacePremiumIcon sx={{ color: subscriptionIconColor }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Notifications - Premium UI */}
          <Tooltip title={t('notifications.title') || 'Notifications'}>
            <IconButton color="inherit" onClick={handleNotifClick} sx={{ mr: 2, zIndex: 2 }}>
              {/* FIXED RED, WHITE DIGITS — not color="error". The theme measures its
                  own ink, and on Dark Mode the danger colour is a light coral, so it
                  correctly returned a near-black ink and the count stopped being
                  readable as a badge. See packages/core/src/ui/badge.js. */}
              {/* `invisible` is not optional here, and MUI is the reason. useBadge.js:26
                  hides a badge only when `badgeContent === 0` — STRICT, against the
                  number. formatBadgeCount returns '' for nothing-unread, which is
                  not 0, so the badge stayed on and drew an empty red disc over the
                  icon whether or not anything was waiting. It reads as "you have
                  something" and there is nothing. */}
              <Badge
                badgeContent={formatBadgeCount(unreadCount)}
                invisible={!(Number(unreadCount) > 0)}
                sx={{ '& .MuiBadge-badge': { bgcolor: UNREAD_BADGE.bg, color: UNREAD_BADGE.fg, fontWeight: 700 } }}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Messages - Unread badge + dropdown */}
          <Tooltip title={t('header.messages') || 'Messages'}>
            <IconButton color="inherit" onClick={handleMsgClick} sx={{ mr: 1, zIndex: 2 }}>
              <Badge
                badgeContent={formatBadgeCount(msgUnread)}
                invisible={!(Number(msgUnread) > 0)}
                sx={{ '& .MuiBadge-badge': { bgcolor: UNREAD_BADGE.bg, color: UNREAD_BADGE.fg, fontWeight: 700 } }}>
                <MailIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile */}
          <Tooltip title={safeUserName || toDisplayText(t('header.profile'), 'Profile')}>
            <IconButton color="inherit" onClick={handleProfileClick} sx={{ ml: 1, p: 0, zIndex: 2 }}>
              <Avatar
                src={avatarSrc}
                sx={{
                  bgcolor: user?.avatarUrl ? undefined : deepOrange[500],
                  color: '#fff',
                  width: 40,
                  height: 40,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  border: `2px solid ${theme.palette.secondary.main}`,
                  boxShadow: '0 2px 8px rgba(44,56,126,0.10)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(44,56,126,0.18)',
                  },
                }}
                alt={safeUserName}
              >
                {!user?.avatarUrl && getInitials(user?.name)}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={notifAnchor}
            open={notifOpen}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                width: 'min(380px, calc(100vw - 24px))',
                borderRadius: 3,
                overflow: 'hidden',
                overflowX: 'hidden'
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="h6" sx={{ color: theme.palette.secondary.main, fontWeight: "bold" }}>
                {t('notifications.title') || 'Notifications'}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2, maxHeight: 420, overflowY: "auto", overflowX: 'hidden' }}>
              {notifLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notifications.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {t("notifications.none") || "No notifications yet."}
                </Typography>
              ) : (
                notifications.map((notif) => (
                  <MenuItem
                    key={notif.id}
                    onClick={() => { markNotificationRead(notif.id); handleNotifClose(); }}
                    sx={{
                      alignItems: "flex-start",
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      '& .MuiTypography-root': {
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                      },
                      background: notif.read ? "none" : `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, #fffbe6 100%)`,
                      borderRadius: 2,
                      mb: 1,
                      boxShadow: notif.read ? "none" : "0 2px 8px rgba(250,204,21,0.10)"
                    }}
                  >
                    <Box sx={{ minWidth: 0, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                        <Typography
                          variant="body1"
                          fontWeight={notif.read ? 500 : 700}
                          color={notif.read ? "text.primary" : theme.palette.secondary.main}
                        >
                          {toDisplayText(notif.title, toDisplayText(t("notifications.defaultTitle"), "Notification"))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {(() => {
                            const p = (notif && typeof notif.payload === 'object' && notif.payload) ? notif.payload : null;
                            const role = (p?.sourceRole || p?.senderRole || p?.role || notif?.sourceRole || notif?.role || '');
                            return role ? String(role) : 'System';
                          })()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {/* The sentence comes from packages/core, shared with
                            the phone. This fell back to `|| notif.type`, which
                            put `marks_recorded` in front of a parent — a
                            developer's word for an event. The server sends
                            facts; the wording is built from them, so switching
                            language re-reads old notifications too. */}
                        {toDisplayText(describeNotification(notif, t), '')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {notif.createdAt ? new Date(notif.createdAt).toLocaleString() : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Box>
          </Menu>

          <Dialog open={subscriptionOpen} onClose={handleCloseSubscription} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkspacePremiumIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Subscription Status</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {subscriptionUi?.effectiveStatus && (
                  <Chip
                    label={subscriptionUi.statusLabel || subscription?.status || '-'}
                    size="small"
                    color={
                      subscriptionUi.effectiveStatus === 'active' ? 'success' :
                      subscriptionUi.effectiveStatus === 'trial' ? 'info' :
                      subscriptionUi.effectiveStatus === 'grace' ? 'warning' :
                      subscriptionUi.effectiveStatus === 'suspended' ? 'error' :
                      subscriptionUi.effectiveStatus === 'expired' ? 'error' : 'default'
                    }
                    sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                  />
                )}
                <IconButton aria-label="Close" onClick={handleCloseSubscription} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {subscriptionError && (
                <Alert severity="error" sx={{ mb: 2 }}>{subscriptionError}</Alert>
              )}
              {subscriptionLoading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {subscriptionUi?.actionLabel && (
                    <Alert severity={subscriptionUi?.severity || 'info'} sx={{ mb: 2 }}>
                      {subscriptionUi.actionLabel}
                    </Alert>
                  )}

                  {/* Billing Amount Card */}
                  {subscriptionBilling && (
                    <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, p: 2, mb: 2, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>Subscription Amount</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ my: 0.5 }}>
                        {Number(subscriptionBilling.totalAmount || 0).toLocaleString()} <Typography component="span" variant="body2">{subscriptionBilling.currency || 'XAF'}</Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        {subscriptionBilling.studentCount || 0} students × {Number(subscriptionBilling.pricePerStudent || 0).toLocaleString()} {subscriptionBilling.currency || 'XAF'}/student
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5 }}>
                        Academic Year: {subscriptionBilling.academicYearName || '-'}
                      </Typography>
                    </Box>
                  )}

                  {/* Plan & Duration Details */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Plan</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {subscription?.SubscriptionPlan?.name || (subscription?.planId ? `Plan #${subscription.planId}` : 'No Plan')}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Billing Cycle</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                        {subscription?.SubscriptionPlan?.billingCycle || 'Annual'}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Start Date</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {subscription?.startDate ? new Date(subscription.startDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">End Date</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  {subscriptionUi?.effectiveStatus === 'grace' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Grace period ends: <strong>{subscriptionUi?.graceEndMs ? new Date(subscriptionUi.graceEndMs).toLocaleDateString() : '-'}</strong>
                    </Alert>
                  )}

                  {/* Time Remaining */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{subscriptionUi?.deadlineLabel || 'Expires'} in</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: subscriptionUi?.severity === 'error' ? 'error.main' : subscriptionUi?.severity === 'warning' ? 'warning.main' : 'success.main' }}>
                      {subscriptionUi?.timeLeftText || '-'}
                    </Typography>
                  </Box>

                  {subscriptionUi?.periodBounds?.percent != null && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={subscriptionUi.periodBounds.percent}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {Number.isFinite(subscriptionUi?.periodBounds?.elapsedDays) ? `${subscriptionUi.periodBounds.elapsedDays} days elapsed` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number.isFinite(subscriptionUi?.periodBounds?.remainingDays) ? `${subscriptionUi.periodBounds.remainingDays} days left` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Latest Invoice */}
                  {subscriptionInvoice && (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Latest Invoice</Typography>
                        <Chip
                          label={subscriptionInvoice.status || '-'}
                          size="small"
                          variant="outlined"
                          color={subscriptionInvoice.status === 'paid' ? 'success' : subscriptionInvoice.status === 'issued' ? 'warning' : 'default'}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                        {Number(subscriptionInvoice.amount || 0).toLocaleString()} XAF
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{subscriptionInvoice.invoiceNumber} • Due: {subscriptionInvoice.dueDate ? new Date(subscriptionInvoice.dueDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  )}

                  {/* Payments Summary */}
                  {subscriptionPaymentsSummary && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                      <Typography variant="caption" color="text.secondary">Total Paid to Date</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {Number(subscriptionPaymentsSummary.totalPaid || 0).toLocaleString()} {subscriptionPaymentsSummary.currency || 'XAF'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              <Button onClick={loadSubscription} disabled={subscriptionLoading} variant="outlined" size="small">Refresh</Button>
              <Button onClick={() => { handleCloseSubscription(); navigate('/finance/subscription-renewal'); }} variant="contained" color="primary">
                Renew Subscription
              </Button>
            </DialogActions>
          </Dialog>
          {/* Messages dropdown */}
          <Menu
            anchorEl={msgAnchor}
            open={msgOpen}
            onClose={handleMsgClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                width: 'min(420px, calc(100vw - 24px))',
                borderRadius: 3,
                overflow: 'hidden',
                overflowX: 'hidden'
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="h6" sx={{ color: theme.palette.secondary.main, fontWeight: "bold" }}>
                {t('header.messages') || 'Messages'}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2, maxHeight: 460, overflowY: "auto", overflowX: 'hidden' }}>
              {msgLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (msgList || []).length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {t('header.noMessages') || 'No messages yet.'}
                </Typography>
              ) : (
                msgList.map((m) => (
                  <MenuItem
                    key={m.id}
                    onClick={async () => {
                      // mark read then navigate
                      try {
                        await fetchJson(`/api/messages/${m.id}/read`, { method: 'PATCH' });
                        await loadMsgUnreadCount();
                      } catch (_) {}
                      handleMsgClose();
                      navigate(`/communication/messages?tab=inbox&messageId=${encodeURIComponent(String(m.id))}`);
                    }}
                    sx={{
                      alignItems: 'flex-start',
                      mb: 0.5,
                      borderRadius: 2,
                      bgcolor: m.readAt ? 'transparent' : 'rgba(25, 118, 210, 0.06)',
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      '& .MuiTypography-root': {
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.25, width: '100%', minWidth: 0 }}>
                      <Avatar
                        src={resolveMediaUrl(m.fromAvatarUrl || m.fromUserAvatarUrl)}
                        sx={{ width: 34, height: 34, flexShrink: 0 }}
                      >
                        {((m.fromName || m.fromUserName || 'M').toString().trim().slice(0, 1) || 'M').toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: m.readAt ? 600 : 800, lineHeight: 1.2 }}>
                          {toDisplayText(m.fromName || m.fromUserName || m.senderName, 'Sender')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                          {toDisplayText(m.fromRole || m.fromUserRole, '')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25, mt: 0.5 }}>
                          {(m.content || '').toString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleMsgClose(); navigate('/communication/messages?tab=inbox'); }} sx={{ justifyContent: 'center', fontWeight: 600 }}>
              {t('header.viewAllMessages') || 'View all messages'}
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={profileAnchor}
            open={profileOpen}
            onClose={handleProfileClose}
            disableRestoreFocus
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 260, borderRadius: 3, p: 0.5 } }}
          >
            {/* Premium Profile Header */}
            <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={avatarSrc}
                sx={{
                  bgcolor: user?.avatarUrl ? undefined : deepOrange[500],
                  color: '#fff',
                  width: 56,
                  height: 56,
                  fontWeight: 700,
                  fontSize: '1.3rem',
                  border: `2px solid ${theme.palette.secondary.main}`,
                  boxShadow: '0 2px 8px rgba(44,56,126,0.10)'
                }}
                alt={user?.name}
              >
                {!user?.avatarUrl && getInitials(user?.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {safeUserName || toDisplayText(t('header.user'), 'User')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {safeUserEmail}
                </Typography>
                <Typography variant="caption" color="secondary" sx={{ fontWeight: 500 }}>
                  {safeUserRoleLabel}
                  {user?.role === 'SystemAdmin' ? '' : (toDisplayText(user?.schoolName, '') ? ` • ${toDisplayText(user?.schoolName, '')}` : '')}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem component={Link} to="/my-profile" onClick={handleProfileClose} sx={{ py: 1 }}>
              {t('header.myProfile') || 'My Profile'}
            </MenuItem>
            {/* The people already using the product are the ones who most want
                it on their phone, and this menu is on every page they see.
                Renders nothing until a system administrator sets the link. */}
            <AndroidAppButton variant="menuItem" onOpen={handleProfileClose} />
            {isImpersonating && (
              <MenuItem onClick={handleStopImpersonation} sx={{ py: 1, fontWeight: 600 }}>
                Stop Impersonation
              </MenuItem>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout} sx={{ py: 1, color: 'error.main', fontWeight: 600 }}>
              {t('header.logout') || 'Logout'}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Snackbar
        open={messageSnack.open}
        autoHideDuration={6000}
        onClose={() => setMessageSnack({ open: false, fromName: '', fromAvatarUrl: '', subject: '', preview: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: 'min(420px, calc(100vw - 24px))'
        }}
      >
        <Alert
          onClose={() => setMessageSnack({ open: false, fromName: '', fromAvatarUrl: '', subject: '', preview: '' })}
          severity="info"
          sx={{ width: '100%' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={resolveMediaUrl(messageSnack.fromAvatarUrl)}
              sx={{ width: 28, height: 28 }}
            >
              {(messageSnack.fromName || 'M').toString().slice(0, 1).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {toDisplayText(messageSnack.fromName, toDisplayText(t('messages.newMessage'), 'New message'))}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {toDisplayText(messageSnack.subject, '') ? `${toDisplayText(messageSnack.subject, '')} — ${toDisplayText(messageSnack.preview, '')}` : toDisplayText(messageSnack.preview, '')}
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Snackbar>
      <Snackbar
        open={notifSnack.open}
        autoHideDuration={6000}
        onClose={() => setNotifSnack({ open: false, title: '', message: '', source: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: 'min(420px, calc(100vw - 24px))'
        }}
      >
        <Alert
          onClose={() => setNotifSnack({ open: false, title: '', message: '', source: '' })}
          severity="info"
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {toDisplayText(notifSnack.source, toDisplayText(t('notifications.new'), 'Notification'))}
          </Typography>
          {(notifSnack.message || notifSnack.title) ? (
            <Typography variant="body2" sx={{ lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {toDisplayText(notifSnack.message || notifSnack.title, '')}
            </Typography>
          ) : null}
        </Alert>
      </Snackbar>
      <Snackbar
        open={announceSnack.open}
        autoHideDuration={6000}
        onClose={() => setAnnounceSnack({ open: false, title: '', body: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: 'min(420px, calc(100vw - 24px))'
        }}
      >
        <Alert onClose={() => setAnnounceSnack({ open: false, title: '', body: '' })} severity="info" sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {toDisplayText(announceSnack.title, '')}
            </Typography>
            {announceSnack.body ? (
              <Typography variant="body2" sx={{ lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {toDisplayText(announceSnack.body, '')}
              </Typography>
            ) : null}
          </Box>
        </Alert>
      </Snackbar>
      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : sidebarExpanded}
        onClose={handleMobileDrawerClose}
        ModalProps={{ keepMounted: false }}
        sx={{
          width: isMobile ? 'min(88vw, 320px)' : (sidebarExpanded ? drawerExpandedWidth : drawerCollapsedWidth),
          flexShrink: 0,
          zIndex: isMobile ? theme.zIndex.appBar + 2 : theme.zIndex.drawer,
          '& .MuiDrawer-paper': {
            width: isMobile ? 'min(88vw, 320px)' : (sidebarExpanded ? drawerExpandedWidth : drawerCollapsedWidth),
            boxSizing: 'border-box',
            transition: isMobile ? 'none' : theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            display: "flex",
            flexDirection: "column",
            height: '100vh',
            zIndex: isMobile ? theme.zIndex.appBar + 2 : theme.zIndex.drawer,
            boxShadow: isMobile ? theme.shadows[16] : 'none',

            // THE SIDEBAR IS BLUE, matching the phone's drawer. Scoped to this
            // paper on purpose — a theme-wide MuiDrawer override would repaint
            // the settings and notification drawers with it too.
            //
            // A deep ground needs its own foreground rules, so everything that
            // inherits `text.primary` or a divider colour is restated here.
            // Without that the labels stay near-black on indigo and vanish.
            // Flat, for the same reason as the top bar above: a sidebar told to
            // be one colour should measure that colour anywhere on it.
            backgroundColor: SIDEBAR.bg,
            color: SIDEBAR.text,
            borderRight: 'none',

            '& .MuiDivider-root': { borderColor: SIDEBAR.line },
            '& .MuiListItemIcon-root': { color: SIDEBAR.icon },
            '& .MuiListItemText-primary': { color: SIDEBAR.text },
            '& .MuiTypography-root': { color: SIDEBAR.text },
            '& .MuiIconButton-root': { color: SIDEBAR.icon },
            '& .MuiIconButton-root:hover': { backgroundColor: SIDEBAR.hover },

            '& .MuiListItemButton-root': {
              borderRadius: 10,
              transition: 'background-color 150ms cubic-bezier(0.22,1,0.36,1), padding-left 150ms cubic-bezier(0.22,1,0.36,1)',
              '&:hover': { backgroundColor: SIDEBAR.hover },
            },

            // THE CURRENT PAGE IS A SOLID GOLD FILL. A 14% white tint was a
            // weak signal on a coloured ground — there is far less contrast
            // range to spend than on white — and in a list of twenty items the
            // eye had to hunt for it. A block of the brand's accent does not
            // need hunting for.
            //
            // Its text and icon go INDIGO 900 rather than white: white on gold
            // 500 is 2.69:1 and unreadable, indigo is 5.88:1.
            '& .MuiListItemButton-root.Mui-selected': {
              backgroundColor: SIDEBAR.active,
              '&:hover': { backgroundColor: SIDEBAR.activeHover },
              // The icon drops its group colour and goes white with the
              // label — a coloured glyph on gold is two accents fighting, and
              // several of the group colours are illegible on it.
              '& .MuiListItemIcon-root': { color: SIDEBAR.activeText },
              '& .MuiListItemIcon-root .MuiSvgIcon-root': { color: SIDEBAR.activeText },
              '& .MuiListItemText-primary': { color: SIDEBAR.activeText, fontWeight: 700 },
              '& .MuiTypography-root': { color: SIDEBAR.activeText },
            },

            // SUBMENUS STEP BACK FROM THEIR PARENT — BY COLOUR ONLY.
            //
            // This block used to set geometry too: the Collapse was inset 8px
            // with a rounded corner, and its buttons took marginInline 4,
            // paddingLeft 28 and minHeight 38. Every one of those outranks the
            // submenu button's own sx, which asks for pl: 2, minHeight: 40 and
            // `justifyContent: 'center'` — the centring that keeps its icon in
            // the middle when the drawer is collapsed. So submenu rows sat
            // 24px narrower and 12px further right than their parents, and in
            // the collapsed drawer the icons were pushed off centre entirely.
            // That was the distortion.
            //
            // A recessed ground and quieter text say "inside this" perfectly
            // well, and cost no layout. The indent stays where the component
            // asks for it.
            //
            // SCOPED TO `.MuiCollapse-vertical`, NOT `.MuiCollapse-root`.
            //
            // Every TOP-LEVEL item wraps its own label in a horizontal Collapse,
            // so it can slide away when the drawer contracts. A rule on
            // `.MuiCollapse-root` matched those too — painting the recessed
            // submenu ground behind each top-level LABEL but not its icon, and
            // dropping the label to the quieter submenu colour. That is what
            // made the menu look scattered: twenty dark chips floating beside
            // their icons instead of one list. MUI 5 tags the two orientations
            // apart, and only the vertical one is a submenu.
            '& .MuiCollapse-vertical': {
              backgroundColor: SIDEBAR.subBg,
              '& .MuiListItemText-primary': {
                color: SIDEBAR.subText,
              },
              // A submenu that IS the current page still takes the ivory band;
              // the rule above applies here too. This only stops the quieter
              // submenu colour from winning over the band's own ink.
              '& .MuiListItemButton-root.Mui-selected .MuiListItemText-primary': {
                color: SIDEBAR.activeText,
              },
            },
          },
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          minHeight: '64px !important',
          position: 'relative',
          px: isMobile ? 1.5 : 2
        }}>
          {isDrawerExpanded && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${brandingGap ?? 8}px`, minWidth: 0, flex: 1, justifyContent: isMobile ? 'center' : 'flex-start', pr: isMobile ? 5 : 0 }}>
              {resolvedPlatformLogo && brandingDisplay !== 'nameOnly' && (
                <Box
                  component="img"
                  src={resolvedPlatformLogo}
                  alt=""
                  sx={{
                    width: logoSize || 36,
                    height: logoSize || 36,
                    objectFit: 'contain',
                    borderRadius: 0.5
                  }}
                />
              )}
              {brandingDisplay !== 'logoOnly' && (
                <Typography variant="h6" noWrap sx={{
                  ...(platformNameFont ? { fontFamily: platformNameFont } : {}),
                  // The configured name COLOUR is deliberately not applied in
                  // here. It is chosen against a white page — the landing and
                  // login screens still honour it — and a dark choice on the
                  // indigo sidebar is an invisible product name.
                  color: SIDEBAR.text,
                  ...(platformNameFontSize ? { fontSize: platformNameFontSize } : {}),
                  ...(platformNameFontWeight ? { fontWeight: platformNameFontWeight } : { fontWeight: 700 }),
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>{safePlatformName}</Typography>
              )}
            </Box>
          )}
          {isMobile ? (
            <IconButton onClick={handleMobileDrawerClose} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
              <CloseIcon />
            </IconButton>
          ) : (
            <IconButton onClick={toggleSidebar}>
              {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </Toolbar>
        <Divider />
        
        {/* Menu Items */}
        <List sx={{ flexGrow: 1, py: 0, overflowY: 'auto' }}>
          {menuItems.map((item, idx) => {
            const roleNormForMenu = (user?.roleNormalized || user?.role || '').toLowerCase().replace(/\s+/g, '');
            const roleAllowsDropdowns = ['schooladmin','principal','headmaster','principalheadteacher','systemadmin','superadmin'].includes(roleNormForMenu);
            const hasSubMenus = roleAllowsDropdowns && Array.isArray(item.subMenus) && item.subMenus.length > 0;
            const menuLabel = t(`menus.${item.textKey}`) || item.textKey;
            return (
              <React.Fragment key={item.path || item.textKey}>
                <Tooltip 
                  title={menuLabel}
                  placement="right"
                  disableHoverListener={isDrawerExpanded}
                >
                  <ListItemButton
                    onClick={() => hasSubMenus ? handleToggleMenu(idx) : handleNavigateFromSidebar(item.path)}
                    selected={location.pathname === item.path}
                    sx={{
                      py: 0.8,
                      px: 2,
                      minHeight: 48,
                      justifyContent: isDrawerExpanded ? 'initial' : 'center',
                      '&.Mui-selected': {
                        // Left to the sidebar block above; action.selected is a
                        // translucent black meant for a white surface.
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: isDrawerExpanded ? 2 : 'auto', justifyContent: 'center' }}>
                      {React.isValidElement(item.icon)
                        ? React.cloneElement(item.icon, { sx: { color: getMenuIconColor(item.textKey) } })
                        : item.icon}
                    </ListItemIcon>
                    <Collapse in={isDrawerExpanded} orientation="horizontal">
                      <ListItemText 
                        primary={menuLabel}
                        sx={{ minWidth: 0, flex: 1 }}
                        primaryTypographyProps={{ 
                          fontSize: "0.9rem",
                          fontWeight: location.pathname === item.path ? 600 : "normal",
                          noWrap: true,
                          sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                        }} 
                      />
                    </Collapse>
                    {hasSubMenus && isDrawerExpanded && (
                      <ChevronRightIcon sx={{ transform: openMenus[idx] ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                    )}
                  </ListItemButton>
                </Tooltip>
                {hasSubMenus && (
                  <Collapse in={openMenus[idx] || !isDrawerExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subMenus.map(sub => {
                        const subLabel = t(`menus.${sub.textKey}`) || sub.textKey;
                        return (
                          <Tooltip key={sub.path} title={subLabel} placement="right">
                            <ListItemButton
                              component={Link}
                              to={sub.path}
                              onClick={handleMobileDrawerClose}
                              selected={location.pathname === sub.path}
                              sx={{
                                pl: 2,
                                py: 0.7,
                                minHeight: 40,
                                justifyContent: 'center',
                                background: 'transparent'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: 0, justifyContent: 'center',px: 2 }}>
                                {React.isValidElement(sub.icon)
                                  ? React.cloneElement(sub.icon, { sx: { color: getMenuIconColor(item.textKey) } })
                                  : sub.icon}
                              </ListItemIcon>
                              {isDrawerExpanded && (
                                <ListItemText
                                  primary={subLabel}
                                  sx={{ minWidth: 0, flex: 1 }}
                                  primaryTypographyProps={{
                                    fontSize: '0.88rem',
                                    noWrap: true,
                                    sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </Tooltip>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
        
        {/* THE FOOT OF THE SIDEBAR — the app download, then the version.
            `mt: auto` is what actually pins it there. The menu above has
            flexGrow, which pushes this down only while there are enough items
            to fill the column; a role with three pages left the rest of the
            height to be shared out, and the download row stretched into a
            button the height of a hand. Margin auto takes the free space
            instead, so the row is its own height at the bottom whether the menu
            has three entries or thirty.

            `flexShrink: 0` for the opposite case: a long menu in a short window
            must scroll rather than squash the version out of existence. */}
        <Box sx={{ mt: 'auto', flexShrink: 0 }}>
          {/* Not a page of this product — a way of leaving it for a better one
              on a phone — so it sits below the navigation rather than in it.
              Collapses to its icon with the drawer, and renders nothing at all
              until a system administrator has said where the app is. */}
          <AndroidAppButton
            variant="sidebar"
            expanded={isDrawerExpanded}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
          />

          <Box sx={{
            p: 1,
            textAlign: "center",
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Collapse in={isDrawerExpanded} orientation="horizontal">
              <Typography variant="caption" color="text.secondary">
                v{process.env.REACT_APP_VERSION || "1.0.0"}
              </Typography>
            </Collapse>
          </Box>
        </Box>
      </Drawer>

      {/* Academic Year Bar below AppBar.
          Gated by yearBarVisible — every school portal, system roles excluded.
          See the note on that flag for why this is safe now and was not before:
          the endpoints that silently emptied when X-Academic-Year-Id started
          arriving now drop the year and retry, as the marks and class-list
          endpoints already did. */}
      {yearBarVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: { xs: 0, sm: `${sidebarOffset}px` },
            right: 0,
            zIndex: theme.zIndex.appBar - 1,
            bgcolor: 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            px: { xs: 1, sm: 2 },
            py: 0.75,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 1.5 }
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: { xs: 'none', sm: 'block' }, minWidth: 100, whiteSpace: 'nowrap' }}>
            {t('common.academicYear', 'Academic Year')}:
          </Typography>
          <Box sx={{ minWidth: 0, flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 220px' }, maxWidth: { xs: 'calc(50% - 4px)', sm: 220 } }}>
            <YearSelector size="small" fullWidth />
          </Box>
          {/* PhaseSwitcher self-hides for single-phase schools */}
          {user?.role === 'SchoolAdmin' && (
            <Box sx={{ minWidth: 0, flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 200px' }, maxWidth: { xs: 'calc(50% - 4px)', sm: 200 } }}>
              <PhaseSwitcher size="small" />
            </Box>
          )}

          {/* READ-ONLY, SAID ONCE, ON EVERY PAGE.
              A finished session can be looked at but not edited — the server
              refuses every write with "Past academic years are read-only" — and
              until now the web said nothing at all, so the first sign was a
              failed save. It belongs beside the year selector because that is
              the control that put the person here, and putting it here covers
              every page rather than the handful somebody remembered to mark.
              The phone shows the same line in its drawer, under the same
              chip. */}
          {viewingPastYear && (
            <Chip
              size="small"
              icon={<HistoryIcon />}
              label={t('common.readOnlyYear', 'Read only — a finished year')}
              color="warning"
              variant="outlined"
              sx={{ ml: { xs: 0, sm: 1 }, fontWeight: 600 }}
            />
          )}
        </Box>
      )}

      {/* Expired/grace banner (trial/subscription ended but not yet suspended) */}
      <Collapse in={showExpiredBanner}>
        <Box
          sx={{
            position: 'fixed',
            top: yearBarVisible ? 104 : 64,
            left: { xs: 0, sm: `${sidebarOffset}px` },
            right: 0,
            zIndex: theme.zIndex.appBar - 2,
            px: 2,
            pt: 1
          }}
        >
          <Alert
            severity={subscriptionUi?.effectiveStatus === 'grace' ? 'warning' : 'error'}
            action={
              canManageSubscription ? (
                <Button
                  variant="contained"
                  color={subscriptionUi?.effectiveStatus === 'grace' ? 'warning' : 'error'}
                  size="small"
                  onClick={handleOpenSubscription}
                >
                  Renew Subscription
                </Button>
              ) : null
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {subscriptionUi?.isTrial ? 'Free trial expired.' : 'Subscription expired.'}
            </Typography>
            <Typography variant="body2">
              {subscriptionUi?.effectiveStatus === 'grace'
                ? 'You are in the grace period. Renew before grace ends to avoid suspension.'
                : 'Renew to restore full access.'}
            </Typography>
          </Alert>
        </Box>
      </Collapse>

      {/* Suspended school banner (shown immediately after login) */}
      {isSchoolSuspended && (
        <Box
          sx={{
            position: 'fixed',
            top: yearBarVisible ? 104 : 64,
            left: { xs: 0, sm: `${sidebarOffset}px` },
            right: 0,
            zIndex: theme.zIndex.appBar - 2,
            px: 2,
            pt: 1
          }}
        >
          <Alert
            severity="error"
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => navigate('/finance/subscription-renewal')}
                >
                  Renew Subscription
                </Button>
              </Box>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Your school is suspended.
            </Typography>
            <Typography variant="body2">
              Please renew your subscription to regain access.
              {suspensionReason ? ` Reason: ${suspensionReason}` : ''}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Main Content Area */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'hidden',
        width: { xs: '100%', sm: `calc(100% - ${sidebarOffset}px)` },
        ml: { xs: 0, sm: `${sidebarOffset}px` },
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        height: 'calc(100vh - 64px)',
        maxWidth: '100vw',
        overflowX: 'hidden',
        // Offset for AppBar + Year bar when visible (push content fully below fixed bars)
        mt: (() => {
          const base = yearBarVisible ? 13 : 8;
          // Add extra space for banner heights
          if (isSchoolSuspended) return base + 9;
          if (showExpiredBanner) return base + 9;
          return base;
        })(),
      }}>
        {/* Scrollable Content Container */}
        <Box sx={{ 
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        
          // Ensures the footer stays at the bottom of the content
          minHeight: '100%',
        }}>
          {/* Page Content */}
          {canAccessCurrentPage() ? (
            <Container 
              maxWidth={false}
              disableGutters={isSuperAdminRoute || isMobile}
              sx={{ 
                width: "100%",
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                px: isSuperAdminRoute ? 0 : { xs: 0, sm: 2, md: 4 },
                maxWidth: isSuperAdminRoute ? 'none' : { xs: 'none', sm: '2000px' },
                margin: isSuperAdminRoute ? 0 : { xs: 0, sm: '0 auto' },

                // FULL WIDTH ON A PHONE, WITH A GUTTER — and the two are not
                // in conflict.
                //
                // The pages themselves open with `px: { xs: 2 }` or a
                // `<Container maxWidth="xl">`, each reasonable on a laptop and
                // each costing 16-24px a side on a 360px screen. Overriding them
                // here is one rule on the element that actually knows how wide
                // the screen is, rather than a hundred edits.
                //
                // BUT NOT TO ZERO. Reaching the glass on both sides is not "full
                // width", it is text against the bezel: labels lose their
                // breathing room and a card edge stops reading as an edge. A
                // small symmetric gutter keeps almost all of the width and still
                // lets the content sit inside something.
                //
                // Written as selectors rather than props because MUI emits its
                // own padding-left/right and a prop on the parent does not beat
                // them. Vertical padding is left alone; it is not what runs out.
                ...(isMobile && !isSuperAdminRoute && {
                  '& > .MuiContainer-root': {
                    paddingLeft: MOBILE_GUTTER,
                    paddingRight: MOBILE_GUTTER,
                    maxWidth: '100%',
                  },
                  '& > .MuiBox-root': {
                    paddingLeft: MOBILE_GUTTER,
                    paddingRight: MOBILE_GUTTER,
                  },

                  // THE GRIDS ARE LEFT ALONE, deliberately, and the previous
                  // attempt here is what made one margin wider than the other.
                  //
                  // MUI carries a Grid container's gutters as a NEGATIVE margin
                  // (Grid.js:224-225): `width: calc(100% + 16px); margin-left:
                  // -16px`, with a matching `padding-left` on each item. Work it
                  // through against a parent whose content box is [P, P+W]:
                  //
                  //   container spans  [P-16, P+W]
                  //   item content     starts at (P-16)+16 = P, ends at P+W
                  //
                  // So the CONTENT is already symmetric, at exactly P on both
                  // sides. Only the container's own transparent box hangs past
                  // the left edge, and `overflowX: clip` below contains it.
                  //
                  // Forcing `marginLeft: 0` — what was here — left the item
                  // padding in place with nothing to cancel it, so content
                  // started at P+16 on the left and P+W on the right. Sixteen
                  // pixels of difference, on every one of the 68 pages that
                  // opens with a grid.
                }),

                // THE GUARANTEE, independent of the rules above. `clip` and not
                // `hidden`: hidden makes this a scroll container, which breaks
                // every `position: sticky` header inside it. clip just refuses
                // to paint outside the box.
                ...(isMobile && { overflowX: 'clip' }),
              }}
            >
              {/* Finance data is cached so it stays printable offline, which means an
                  offline view can show a balance that is no longer current. Placed here
                  rather than per-page so no finance screen — including ones added later
                  — can serve cached money figures without saying so. */}
              {isFinanceRoute && (
                <StaleDataNotice prefixes={FINANCE_CACHE_PREFIXES} />
              )}
              {/* KEYED ON THE SCOPE, so switching the academic year or the
                  phase remounts the page instead of leaving it showing the
                  session it was opened in. See hooks/useScopeKey.js — the
                  alternative was a listener on every page, and the pages that
                  did not have one were the whole complaint. */}
              <Outlet key={scopeKey} context={{ themeName, onThemeChange }} />
            </Container>
          ) : (
            <Container maxWidth={false} disableGutters={isMobile} sx={{ py: 4, textAlign: 'center', px: { xs: 0, sm: 2, md: 4 } }}>
              <Typography variant="h4" color="error" gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You don't have permission to access this page.
              </Typography>
            </Container>
          )}
          
          {/* Footer at Bottom of Content */}
          <Box 
            component="footer"
            sx={{ 
              mt: 'auto', // Pushes footer to bottom
              py: 2,
              px: { xs: 0, sm: 3 },
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <Typography variant="body2" color="text.secondary">
                &copy; {new Date().getFullYear()} {platformName || t("appName")}. {t("All rights reserved.")}
              </Typography>
              <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 2 }}>
                  {t("Version")} {process.env.REACT_APP_VERSION || "1.0.0"}
                </Typography>
                <Typography 
                  variant="body2" 
                  component={Link} 
                  to="/privacy" 
                  color="text.secondary" 
                  sx={{ mr: 2, textDecoration: "none" }}
                >
                  {t("Privacy Policy")}
                </Typography>
                <Typography 
                  variant="body2" 
                  component={Link} 
                  to="/terms" 
                  color="text.secondary" 
                  sx={{ textDecoration: "none" }}
                >
                  {t("Terms of Service")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}